import React from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetDirections } from "~/entity/Directions";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { headerTableDirections } from "~/pages/Editor/EditorDirectories/options";
import { OpenModal } from "~/pages/Editor/type";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";

import DeleteDirectionModal from "./DirectionsManagement/DeleteDirectionModal";
import DirectionsForm from "./DirectionsManagement/DirectionsForm";
import DirectionsFilter from "./Filter";
import { getEditorDirectionsColumns } from "./model/tableAdapter";

import s from "./EditorDirections.less";

const formId = "DirectionsForm";

const EditorDirections = () => {
  const { t } = useTranslation();
  const goBack = useGoToPreviousPage();
  const setDataReplace = useSetLocationData(true);
  const { hasPermission } = usePermissions();
  const { count, page, event, openModal } = useGetLocationData();

  const { data, total, isLoading, refetch } = useGetDirections({
    page: page ? page + 1 : 1,
    count: count ?? 25,
    ActualOn: event || format(new Date(), "yyyy-MM-dd"),
  });

  const closeModal = () => setDataReplace("openModal", "", "currentId", "");
  const openAddModal = () => setDataReplace("openModal", OpenModal.Add);

  const columns = getEditorDirectionsColumns(t, headerTableDirections);
  const { handleCount, handlePage } = usePaginationParams();

  const handleRowEdit = (record: { id?: string | number }) => {
    setDataReplace("openModal", OpenModal.Edit, "currentId", record.id);
  };

  const handleRowDelete = (record: { id?: string | number }) => {
    setDataReplace("openModal", OpenModal.Delete, "currentId", record.id);
  };

  return (
    <Wrapper.Content
      title={t("editor.directions")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      filterComponent={<DirectionsFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("directions.directions.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2
        columns={columns}
        dataSource={data}
        wrapperClassName={s.editor_table}
        loading={isLoading}
        {...(hasPermission("directions.directions.update")
          ? { onRowEdit: handleRowEdit }
          : {})}
        {...(hasPermission("directions.directions.delete")
          ? { onRowDelete: handleRowDelete }
          : {})}
      />
      <Pagination
        count={count ?? 25}
        page={page + 1}
        totalCount={total ?? 0}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        open={openModal === OpenModal.Add || openModal === OpenModal.Edit}
        title={t(
          openModal === OpenModal.Edit
            ? "directions.edit_direction"
            : "directions.add_direction",
        )}
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
      >
        <DirectionsForm
          refetch={refetch}
          closeModal={closeModal}
          currentData={data}
          formId={formId}
        />
      </Modal>

      <DeleteDirectionModal
        isOpen={openModal === OpenModal.Delete}
        closeModal={closeModal}
        refetch={refetch}
        currentData={data}
      />
    </Wrapper.Content>
  );
};

export default EditorDirections;
